import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, LogIn, ExternalLink, ShieldOff, Ban, Loader2, ShieldCheck,
  Wrench, AlertTriangle, CheckCircle2, Building2, Users, ShoppingCart,
  TrendingUp, DollarSign, Tag, Package, Key, Mail, Phone, MapPin,
  Globe, CreditCard, UserCircle, Calendar, Infinity, Settings2, Link2,
} from "lucide-react";
import { toast } from "sonner";
import { StatCard } from "./StatCard";

export function RestaurantDetail({
  restaurantDetail,
  detailLoading,
  impersonating,
  auditing,
  fixing,
  blocking,
  auditResult,
  onBack,
  onLoginAs,
  onToggleBlock,
  onRunAudit,
  onRunFix,
  adminAxios,
  onRefreshTab,
}) {
  const [configForm, setConfigForm] = useState(null); // null = collapsed
  const [savingConfig, setSavingConfig] = useState(false);
  const [domainForm, setDomainForm] = useState(null); // null = collapsed
  const [savingDomain, setSavingDomain] = useState(false);

  if (detailLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!restaurantDetail) {
    return <p className="text-center text-muted-foreground py-10">Erro ao carregar dados</p>;
  }

  const { restaurant, stats, owner, users, payment_settings, last_order, referral } = restaurantDetail;

  return (
    <div className="space-y-4" data-testid="restaurant-detail-panel">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        data-testid="back-to-restaurants"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar para lista
      </button>

      {/* Header */}
      <Card className="bg-gray-900 text-white border-0">
        <CardContent className="p-5 flex items-center gap-4">
          {restaurant.logo_url ? (
            <img src={restaurant.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-white/20" />
          ) : (
            <div className="w-14 h-14 bg-emerald-600 rounded-xl flex items-center justify-center text-white font-bold text-xl">
              {restaurant.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h2 className="text-xl font-bold">{restaurant.name}</h2>
            <p className="text-sm text-gray-400">{restaurant.slug} | {restaurant.template || "custom"}</p>
          </div>
          <div className="text-right space-y-2">
            {restaurant.subscription_status === "active" && <Badge className="bg-emerald-500 text-white">Ativo</Badge>}
            {(!restaurant.subscription_status || restaurant.subscription_status === "trial") && <Badge className="bg-amber-500 text-white">Trial</Badge>}
            {restaurant.subscription_status === "suspended" && <Badge className="bg-red-500 text-white">Suspenso</Badge>}
            <p className="text-xs text-gray-400 mt-1">Plano: {restaurant.plan || "Trial"}</p>
          </div>
        </CardContent>
      </Card>

      {/* Login as Restaurant Button */}
      <Button
        onClick={() => onLoginAs(restaurant.id, restaurant.name)}
        disabled={impersonating}
        className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-sm shadow-lg gap-2"
        data-testid="login-as-restaurant-btn"
      >
        {impersonating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-5 h-5" />}
        Entrar como este Restaurante
        <ExternalLink className="w-4 h-4 ml-1 opacity-70" />
      </Button>

      {/* Block / Unblock Button */}
      {restaurant.subscription_status === "suspended" ? (
        <Button
          onClick={() => onToggleBlock(restaurant.id, "suspended")}
          disabled={blocking}
          className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm gap-2"
          data-testid="unblock-restaurant-btn"
        >
          {blocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
          Desbloquear Restaurante (ativar agora)
        </Button>
      ) : (
        <Button
          onClick={() => onToggleBlock(restaurant.id, restaurant.subscription_status)}
          disabled={blocking}
          variant="outline"
          className="w-full h-10 border-red-300 text-red-600 hover:bg-red-50 font-bold text-sm gap-2"
          data-testid="block-restaurant-btn"
        >
          {blocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
          Bloquear Restaurante
        </Button>
      )}

      {/* Audit Section */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => onRunAudit(restaurant.id)}
              disabled={auditing}
              variant="outline"
              className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="audit-restaurant-btn"
            >
              {auditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              Auditoria do Restaurante
            </Button>
            {auditResult && auditResult.issues_found > 0 && (
              <Button
                onClick={() => onRunFix(restaurant.id)}
                disabled={fixing}
                className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="fix-restaurant-btn"
              >
                {fixing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
                Corrigir Automaticamente
              </Button>
            )}
          </div>

          {auditResult && (
            <div className="space-y-2" data-testid="audit-results">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Categorias:</span>
                <span className="font-bold">{auditResult.total_categories}</span>
                <span className="text-muted-foreground ml-2">Produtos:</span>
                <span className="font-bold">{auditResult.total_products}</span>
              </div>
              {auditResult.status === "ok" ? (
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Tudo ok! Nenhum problema encontrado.</span>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-sm font-bold text-amber-700">{auditResult.issues_found} problema(s) encontrado(s):</p>
                  {auditResult.issues.map((issue, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm bg-white rounded-lg px-3 py-2 border border-amber-200">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{issue}</span>
                    </div>
                  ))}
                </div>
              )}
              {auditResult.fixes_applied?.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-bold text-emerald-700">Correcoes aplicadas:</p>
                  {(typeof auditResult.fixes_applied === "object" && !Array.isArray(auditResult.fixes_applied)
                    ? []
                    : auditResult.fixes_applied
                  ).map?.((fix, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>{fix}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Configuração Avançada (subscription + país/moeda) ── */}
      <Card className="border-violet-200 bg-violet-50/20">
        <CardHeader className="pb-2 cursor-pointer" onClick={() =>
          setConfigForm(f => f ? null : {
            subscription_lifetime: restaurant.subscription_lifetime || false,
            subscription_plan: restaurant.subscription_plan || "trial",
            subscription_status: restaurant.subscription_status || "trial",
            country: restaurant.country || "ES",
            currency: restaurant.currency || "EUR",
            locale: restaurant.locale || "es-ES",
          })
        }>
          <CardTitle className="text-sm flex items-center gap-2 text-violet-700">
            <Settings2 className="w-4 h-4" />
            Configuração Avançada
            {restaurant.subscription_lifetime && (
              <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-auto">♾️ Vitalício</Badge>
            )}
            <span className="text-xs text-muted-foreground ml-auto font-normal">
              {configForm ? "▲ fechar" : "▼ abrir"}
            </span>
          </CardTitle>
        </CardHeader>

        {configForm && (
          <CardContent className="space-y-4 pt-0">
            {/* Lifetime toggle */}
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <p className="text-sm font-semibold text-amber-800 flex items-center gap-1">
                  <Infinity className="w-4 h-4" /> Assinatura Vitalícia
                </p>
                <p className="text-xs text-amber-600">Acesso total permanente. Bypass total de billing.</p>
              </div>
              <button
                onClick={() => setConfigForm(f => ({ ...f, subscription_lifetime: !f.subscription_lifetime }))}
                className={`w-12 h-6 rounded-full transition-colors ${configForm.subscription_lifetime ? "bg-amber-500" : "bg-gray-300"}`}
                data-testid="lifetime-toggle"
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${configForm.subscription_lifetime ? "translate-x-6" : "translate-x-0"}`} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Plano */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Plano</label>
                <Select value={configForm.subscription_plan} onValueChange={v => setConfigForm(f => ({ ...f, subscription_plan: v }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="plan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="basic">Básico (39€)</SelectItem>
                    <SelectItem value="pro">Pro (79€)</SelectItem>
                    <SelectItem value="enterprise">Premium (189€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Status</label>
                <Select value={configForm.subscription_status} onValueChange={v => setConfigForm(f => ({ ...f, subscription_status: v }))}>
                  <SelectTrigger className="h-8 text-sm" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="past_due">Pagamento Pendente</SelectItem>
                    <SelectItem value="suspended">Suspenso</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* País */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">País</label>
                <Select value={configForm.country} onValueChange={v => {
                  const currencyMap = { ES: "EUR", PT: "EUR", BR: "BRL", US: "USD", GB: "GBP" };
                  const localeMap = { ES: "es-ES", PT: "pt-PT", BR: "pt-BR", US: "en-US", GB: "en-GB" };
                  setConfigForm(f => ({ ...f, country: v, currency: currencyMap[v] || "EUR", locale: localeMap[v] || "es-ES" }));
                }}>
                  <SelectTrigger className="h-8 text-sm" data-testid="country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ES">🇪🇸 Espanha (EUR)</SelectItem>
                    <SelectItem value="PT">🇵🇹 Portugal (EUR)</SelectItem>
                    <SelectItem value="BR">🇧🇷 Brasil (BRL)</SelectItem>
                    <SelectItem value="US">🇺🇸 EUA (USD)</SelectItem>
                    <SelectItem value="GB">🇬🇧 Reino Unido (GBP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Moeda (auto-fill, read-only) */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Moeda (auto)</label>
                <div className="h-8 px-3 flex items-center text-sm bg-muted rounded-md border font-mono">
                  {configForm.currency}
                </div>
              </div>
            </div>

            <Button
              onClick={async () => {
                if (!adminAxios) return;
                setSavingConfig(true);
                try {
                  await adminAxios.patch(`/platform-admin/restaurants/${restaurant.id}/subscription`, configForm);
                  toast.success("Configuração guardada com sucesso");
                  setConfigForm(null);
                  if (onRefreshTab) onRefreshTab();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Erro ao guardar");
                } finally {
                  setSavingConfig(false);
                }
              }}
              disabled={savingConfig}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white gap-2"
              data-testid="save-config-btn"
            >
              {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Guardar Configuração
            </Button>
          </CardContent>
        )}
      </Card>

      {/* ── Domínio Personalizado ── */}
      <Card className="border-blue-200 bg-blue-50/20">
        <CardHeader className="pb-2 cursor-pointer" onClick={() =>
          setDomainForm(f => f !== null ? null : (restaurant.custom_domain || ""))
        }>
          <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
            <Link2 className="w-4 h-4" />
            Domínio Personalizado
            {restaurant.custom_domain && (
              <span className="font-mono text-xs text-blue-600 ml-1">{restaurant.custom_domain}</span>
            )}
            {restaurant.domain_status === "active" && (
              <Badge className="bg-green-100 text-green-700 text-[10px] ml-auto">Ativo</Badge>
            )}
            {!restaurant.custom_domain && (
              <Badge className="bg-gray-100 text-gray-500 text-[10px] ml-auto">Não configurado</Badge>
            )}
            <span className="text-xs text-muted-foreground font-normal ml-auto">
              {domainForm !== null ? "▲ fechar" : "▼ configurar"}
            </span>
          </CardTitle>
        </CardHeader>
        {domainForm !== null && (
          <CardContent className="space-y-3 pt-0">
            <p className="text-xs text-muted-foreground">
              Configure o domínio personalizado do restaurante. O DNS deve apontar para o servidor Kaiso.
            </p>
            <div className="flex gap-2">
              <Input
                placeholder="ex: kaisosushicordoba.com"
                value={domainForm}
                onChange={e => setDomainForm(e.target.value.toLowerCase().trim())}
                className="h-9 text-sm font-mono"
              />
              <Button
                onClick={async () => {
                  if (!domainForm.trim()) return;
                  setSavingDomain(true);
                  try {
                    await adminAxios.patch(`/platform-admin/restaurants/${restaurant.id}/custom-domain`, {
                      custom_domain: domainForm.trim(),
                    });
                    toast.success(`Domínio configurado: ${domainForm}`);
                    setDomainForm(null);
                    if (onRefreshTab) onRefreshTab();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Erro ao configurar domínio");
                  } finally {
                    setSavingDomain(false);
                  }
                }}
                disabled={savingDomain || !domainForm.trim()}
                className="h-9 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 gap-1.5 whitespace-nowrap"
              >
                {savingDomain ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Salvar
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Pedidos totais" value={stats.total_orders} icon={ShoppingCart} />
        <StatCard label="Pedidos este mes" value={stats.orders_this_month} icon={TrendingUp} />
        <StatCard label="Receita total" value={`${stats.total_revenue} EUR`} icon={DollarSign} />
        <StatCard label="Receita do mes" value={`${stats.revenue_this_month} EUR`} icon={DollarSign} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Ticket medio" value={`${stats.ticket_medio} EUR`} icon={Tag} />
        <StatCard label="Clientes unicos" value={stats.unique_customers} icon={Users} />
        <StatCard label="Produtos" value={stats.total_products} icon={Package} />
        <StatCard label="Categorias" value={stats.total_categories} icon={Building2} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Owner / Login Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Key className="w-4 h-4 text-emerald-600" /> Login do Proprietario
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <UserCircle className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Nome:</span>
              <span className="font-medium">{owner.name || "-"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{owner.email || "-"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Key className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Senha:</span>
              <span className="text-xs text-muted-foreground">{owner.has_password ? "Definida (hash protegido)" : "Nao definida"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Cadastro:</span>
              <span className="text-xs">{owner.created_at ? new Date(owner.created_at).toLocaleDateString() : "-"}</span>
            </div>
          </CardContent>
        </Card>

        {/* Restaurant Info */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Building2 className="w-4 h-4 text-emerald-600" /> Dados do Restaurante
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Telefone:</span>
              <span className="font-medium">{restaurant.phone || "-"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium">{restaurant.email || "-"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">Endereco:</span>
              <span className="text-xs">{restaurant.address || "-"}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Tag className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">CIF/NIF:</span>
              <span className="font-mono text-xs">{restaurant.cif || "-"}</span>
            </div>
            {restaurant.custom_domain && (
              <div className="flex items-center gap-2 text-sm">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Dominio:</span>
                <span className="font-mono text-xs">{restaurant.custom_domain}</span>
                <Badge className="text-[9px]">{restaurant.domain_status}</Badge>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Users */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" /> Usuarios ({users?.length || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(users || []).map((u, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2">
                  <UserCircle className="w-4 h-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs truncate">{u.name || u.email}</p>
                    <p className="text-[10px] text-muted-foreground font-mono">{u.email}</p>
                  </div>
                  <Badge variant="outline" className="text-[9px]">{u.role || "admin"}</Badge>
                </div>
              ))}
              {(!users || users.length === 0) && (
                <p className="text-xs text-muted-foreground text-center py-2">Nenhum usuario</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payments & Extras */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-emerald-600" /> Pagamentos & Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Stripe:</span>
              <Badge className={payment_settings.stripe_enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
                {payment_settings.stripe_enabled ? "Ativo" : "Nao configurado"}
              </Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Bizum:</span>
              <span className="text-xs font-mono">{payment_settings.bizum_phone || "Nao configurado"}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Viva Wallet:</span>
              <Badge className={payment_settings.viva_wallet_enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
                {payment_settings.viva_wallet_enabled ? "Ativo" : "Nao configurado"}
              </Badge>
            </div>
            {last_order && (
              <div className="border-t pt-2 mt-2">
                <p className="text-[10px] text-muted-foreground mb-1">Ultimo pedido:</p>
                <p className="text-sm font-medium">{last_order.total?.toFixed(2)} EUR - {last_order.status}</p>
                <p className="text-[10px] text-muted-foreground">{last_order.created_at ? new Date(last_order.created_at).toLocaleString() : ""}</p>
              </div>
            )}
            {referral.referred_by && (
              <div className="border-t pt-2 mt-2">
                <p className="text-[10px] text-muted-foreground">Indicado por afiliado: {referral.referral_code || referral.referred_by}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
